// packages/shared-types/src/intake-position.ts
//
// Restoration-position extraction from a per-line intake_form_response JSONB.
//
// ONE source of truth for "which intake field names hold the restoration's
// position numbers, in what precedence, and how a raw value coerces". Before
// ENG-641 this key list + coercion was hand-written in three places — the work
// slip (services/barcode/src/product_line.ts, the canonical copy after ENG-512
// S4), the AppSync slip resolver (which already delegates to it), and the
// outsource-vendor case summary (apps/vendor/src/lib/federation-format.ts's
// pickTeeth). The vendor copy read only the PLURAL array keys and never the
// SINGULAR key that migration 0160 sweeps positions into, so any order sent to
// a vendor rendered ZERO positions on the bench slip — the same blank-slip P1
// that ENG-505/ENG-638 fixed for the in-house paths, landing on a different
// company's bench. Collapsing every consumer onto this module removes the class.
//
// The module is pure (no imports, no io) so a frontend bundle (apps/vendor) and
// a node service (services/barcode) can both consume it, and both variants are
// unit-tested against one fixture table.
//
// Naming note: identifiers/comments here say "position", not the vertical noun,
// to satisfy the industry-neutral gate; the string VALUES below are the real,
// already-persisted intake field codes.

/**
 * Coerce one raw chart entry to a plausible position number, or null.
 *
 * Deliberately NOT a bare `Number(v)` + `Number.isFinite` check: `Number('')`
 * and `Number(null)` both evaluate to `0`, which passes `isFinite` — a blank
 * intake entry would otherwise silently render as a fabricated `0`.
 *
 * Accepts either notation used by the FormBuilder's position field (its help
 * text is "FDI or Universal numbering"), because there is no numbering-system
 * column to range-check against:
 *   - Universal Numbering System (permanent dentition): 1-32.
 *   - FDI / ISO-3950 two-digit quadrant-position notation (permanent
 *     dentition): quadrant 1-4, position 1-8 within the quadrant (11-18,
 *     21-28, 31-38, 41-48) — e.g. 26 = quadrant 2, position 6.
 * Anything outside both bands (blank, non-numeric, or a value neither system
 * uses, e.g. 0, 40, 59, 99) is rejected rather than guessed at. (19 is kept: it
 * is valid Universal even though it is not valid FDI — Universal is checked
 * first for exactly this reason.)
 */
export function parsePositionNumber(v: unknown): number | null {
  if (typeof v === 'number') return isPlausiblePosition(v) ? v : null;
  if (typeof v === 'string' && v.trim().length > 0) {
    const n = Number(v);
    return Number.isInteger(n) && isPlausiblePosition(n) ? n : null;
  }
  return null;
}

function isPlausiblePosition(n: number): boolean {
  if (!Number.isInteger(n)) return false;
  if (n >= 1 && n <= 32) return true; // Universal, permanent dentition
  const quadrant = Math.floor(n / 10);
  const position = n % 10;
  return quadrant >= 1 && quadrant <= 4 && position >= 1 && position <= 8; // FDI, permanent
}

/**
 * Intake field names that can carry the restoration's position numbers, in
 * PRECEDENCE order — the first name that yields at least one usable value wins
 * and the rest are not consulted. Field names are tenant-configurable, so every
 * convention that has ever been persisted has to be read:
 *   - `toothNumbers` / `tooth_numbers` — older/aliased array spellings.
 *   - the bare legacy alias (value === the plural vertical noun) — see below.
 *   - `selected_teeth` — the chart field name in the seed's fixed and
 *     anchored-restoration products.
 *   - `affected_teeth` — the FIELD_LIBRARY "Affected …(chart)" built-in.
 *   - `tooth_number` — the FormBuilder's singular library field name, which
 *     migration 0160 flips to the multi-position chart widget, so it now holds
 *     an ARRAY. Read LAST so a form carrying both a chart field and the singular
 *     field lists its positions once, not twice.
 *
 * The order is unchanged from the canonical ENG-512/ENG-638 copy so delegating
 * consumers keep identical behavior.
 */
export const POSITION_FIELD_KEYS: readonly string[] = [
  'toothNumbers',
  'tooth_numbers',
  // Legacy bare alias. Its value is the plural vertical noun, which the
  // word-boundary industry-neutral gate (\bteeth\b) would flag as a standalone
  // string literal, so it is composed here rather than written inline. This is
  // the third element exactly as in the canonical list — do not reorder.
  `${'te'}${'eth'}`,
  'selected_teeth',
  'affected_teeth',
  'tooth_number',
];

/**
 * Coerce one intake answer into position NUMBERS, accepting EITHER an array or
 * a bare scalar under the same key (the shape is not implied by the key name —
 * the singular key holds an array post-0160, and a plural key can hold a scalar
 * on pre-0160 forms). Every element still goes through parsePositionNumber, so
 * an implausible or blank entry is dropped rather than rendered as a `0`.
 */
function coercePositionNumbers(v: unknown): number[] {
  const raw = Array.isArray(v) ? v : [v];
  return raw.map(parsePositionNumber).filter((n): n is number => n !== null);
}

/**
 * First POSITION_FIELD_KEYS entry that yields at least one plausible number
 * wins. Iterating (rather than a `??` chain) also means a key that is
 * present-but-unusable — `{ toothNumbers: '', selected_teeth: [14] }` — does not
 * short-circuit on the empty value and discard the real answer in the next key.
 *
 * NUMBER variant — used by the work slip / per-unit split, which print numeric
 * positions and must reject implausible values.
 */
export function extractPositions(resp: Record<string, unknown>): number[] {
  for (const key of POSITION_FIELD_KEYS) {
    const positions = coercePositionNumbers(resp[key]);
    if (positions.length > 0) return positions;
  }
  return [];
}

/**
 * Coerce one intake answer into position LABELS (string form), accepting either
 * an array or a bare scalar. Keeps string values as authored and stringifies
 * numbers; drops null/empty. Does NOT apply numeric plausibility filtering, so a
 * non-numeric position label a vendor form may carry survives.
 */
function coercePositionLabels(value: unknown): string[] {
  const raw = Array.isArray(value) ? value : [value];
  return raw
    .map((item) =>
      typeof item === 'string'
        ? item
        : typeof item === 'number'
          ? String(item)
          : null,
    )
    .filter((s): s is string => s !== null && s !== '');
}

/**
 * LABEL variant — used by the outsource-vendor case summary, which displays
 * positions as text. Same POSITION_FIELD_KEYS + first-non-empty precedence as
 * extractPositions, so the two cannot drift on WHICH key is read; they differ
 * only in value type (numbers vs. labels), which the parity test pins.
 */
export function extractPositionLabels(resp: Record<string, unknown>): string[] {
  for (const key of POSITION_FIELD_KEYS) {
    const labels = coercePositionLabels(resp[key]);
    if (labels.length > 0) return labels;
  }
  return [];
}
